import React, { useState } from 'react';
import StepIndicator from 'src/shared/components/step-indicator/StepIndicator';
import OnboardStep from './steps/OnboardStep';
import ImportProjectsStep from './steps/ImportProjectsStep';
import ManifestStep from './steps/ManifestStep';
import { exportSiteManifest } from '../../helpers/exportSiteManifest';
import { useLazyExportSiteManifestQuery } from '../../state/api/organisationEngineSlice';
import type { ImportResult } from '../../hooks/useImportSites.types';

export enum WizardStep {
    Onboard = 0,
    ImportSites = 1,
    SiteManifest = 2,
}

const WIZARD_STEPS = ['Onboard', 'Import Sites', 'Site Manifest'];

type SetupWizardProps = {
    customerName: string;
    currentCustomerId: string;
    onCreateOrganisation: () => Promise<void>;
    isCreatingOrg: boolean;
    createOrgError: string | null;
    projects: Array<{ projectId: string; projectName: string }>;
    onImportSites: () => Promise<void>;
    isImporting: boolean;
    importProgress: { current: number; total: number } | null;
    importResult: ImportResult | null;
    onComplete: () => void;
};

const SetupWizard = ({
    customerName,
    currentCustomerId,
    onCreateOrganisation,
    isCreatingOrg,
    createOrgError,
    projects,
    onImportSites,
    isImporting,
    importProgress,
    importResult,
    onComplete,
}: SetupWizardProps) => {
    const [currentStep, setCurrentStep] = useState<WizardStep>(WizardStep.Onboard);
    const [animState, setAnimState] = useState<'visible' | 'exiting' | 'entering'>('visible');
    const [triggerExportSiteManifest] = useLazyExportSiteManifestQuery();

    const goToNextStep = (nextStep: WizardStep) => {
        setAnimState('exiting');
        setTimeout(() => {
            setCurrentStep(nextStep);
            setAnimState('entering');
            // Next tick: transition to visible (allows browser to register the entering state first)
            setTimeout(() => {
                setAnimState('visible');
            }, 20);
        }, 350);
    };

    const handleOnboard = async () => {
        await onCreateOrganisation();
        goToNextStep(WizardStep.ImportSites);
    };

    // No auto-transition after import — the user sees `ImportResultCallout`
    // on the ImportProjects step and clicks Continue to advance. This forces
    // acknowledgement of partial/total failures instead of silently
    // dropping the result the way the old green Alert did.
    const handleImport = async () => {
        await onImportSites();
    };

    return (
        <div className='flex flex-col items-center justify-center py-16 gap-0' data-testid='setup-wizard'>
            <StepIndicator steps={WIZARD_STEPS} currentStep={currentStep} />

            <div className='w-full max-w-lg my-8 border-t border-grey-20' />

            <div
                className='transition-all duration-500 ease-out'
                style={{
                    opacity: animState === 'visible' ? 1 : 0,
                    transform: animState === 'entering' ? 'translateY(16px)' : 'translateY(0)',
                }}
                data-testid='wizard-step-content'
            >
                {currentStep === WizardStep.Onboard && (
                    <OnboardStep
                        customerName={customerName}
                        currentCustomerId={currentCustomerId}
                        onCreateOrganisation={handleOnboard}
                        isCreating={isCreatingOrg}
                        error={createOrgError}
                    />
                )}
                {currentStep === WizardStep.ImportSites && (
                    <ImportProjectsStep
                        projectCount={projects.length}
                        onImport={handleImport}
                        onContinue={() => goToNextStep(WizardStep.SiteManifest)}
                        isImporting={isImporting}
                        progress={importProgress}
                        result={importResult}
                    />
                )}
                {currentStep === WizardStep.SiteManifest && (
                    <ManifestStep
                        onDownload={() => exportSiteManifest(triggerExportSiteManifest, currentCustomerId)}
                        onFinish={onComplete}
                    />
                )}
            </div>
        </div>
    );
};

export default SetupWizard;
